import { useMemo, useEffect } from 'react';
import CurrencyInput from '@/components/base/CurrencyInput';
import { formatTRY } from '@/lib/currencyFormat';

export type PaymentPlanType = 'single_note' | 'equal_installments' | 'custom_installments';

export interface EqualPlanData {
  totalSaleAmount: string;
  previouslyPaid: string;
  totalInstallmentCount: string;
  paidInstallmentCount: string;
  monthlyInstallmentAmount: string;
  firstInstallmentDate: string;
  paymentDayOfMonth: string;
}

export interface CustomInstallmentRow {
  key: string;
  sequence_no: number;
  due_date: string;
  amount: string;
  paid_amount: string;
  remaining: string;
  status: 'Ödendi' | 'Kısmi Ödendi' | 'Bekliyor' | 'Gecikti';
}

interface LegacyInstallmentPlanProps {
  planType: PaymentPlanType;
  setPlanType: (type: PaymentPlanType) => void;
  equalPlan: EqualPlanData;
  setEqualPlan: React.Dispatch<React.SetStateAction<EqualPlanData>>;
  customInstallments: CustomInstallmentRow[];
  setCustomInstallments: React.Dispatch<React.SetStateAction<CustomInstallmentRow[]>>;
  errors: Record<string, string>;
  setErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  legacyRemainingAmount: number;
  // Note fields for automatic note creation on equal/custom plans
  legacyNoteNo: string;
  setLegacyNoteNo: (v: string) => void;
  legacyBookNo: string;
  setLegacyBookNo: (v: string) => void;
  legacyPageNo: string;
  setLegacyPageNo: (v: string) => void;
  legacySequenceNo: string;
  setLegacySequenceNo: (v: string) => void;
}

export const INITIAL_EQUAL_PLAN: EqualPlanData = {
  totalSaleAmount: '',
  previouslyPaid: '0',
  totalInstallmentCount: '',
  paidInstallmentCount: '0',
  monthlyInstallmentAmount: '',
  firstInstallmentDate: '',
  paymentDayOfMonth: '1',
};

export const INITIAL_CUSTOM_INSTALLMENT: Omit<CustomInstallmentRow, 'key' | 'sequence_no'> = {
  due_date: '',
  amount: '',
  paid_amount: '0',
  remaining: '',
  status: 'Bekliyor',
};

const STATUS_OPTIONS: { value: CustomInstallmentRow['status']; label: string }[] = [
  { value: 'Ödendi', label: 'Ödendi' },
  { value: 'Kısmi Ödendi', label: 'Kısmi Ödendi' },
  { value: 'Bekliyor', label: 'Bekliyor' },
  { value: 'Gecikti', label: 'Gecikti' },
];

const inputClass = 'w-full px-3 py-2.5 text-sm sm:text-base bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 text-foreground-800 placeholder:text-foreground-300';
const inputErrorClass = 'w-full px-3 py-2.5 text-sm sm:text-base bg-red-50 border border-red-300 rounded-lg focus:outline-none focus:border-red-400 focus:ring-1 focus:ring-red-200 text-foreground-800 placeholder:text-foreground-300';
const selectClass = 'w-full px-3 py-2.5 text-sm sm:text-base bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 text-foreground-800 cursor-pointer';
const labelClass = 'text-xs font-medium text-foreground-500 mb-1.5 block';
const errorTextClass = 'text-xs text-red-500 mt-1';
const readonlyClass = 'w-full px-3 py-2.5 text-sm sm:text-base bg-background-100 border border-background-200 rounded-lg text-foreground-600 cursor-not-allowed';
const helperTextClass = 'text-[10px] text-foreground-300 mt-0.5';

function generateKey(): string {
  return `ci_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

export default function LegacyInstallmentPlan({
  planType,
  setPlanType,
  equalPlan,
  setEqualPlan,
  customInstallments,
  setCustomInstallments,
  errors,
  setErrors,
  legacyRemainingAmount,
  legacyNoteNo,
  setLegacyNoteNo,
  legacyBookNo,
  setLegacyBookNo,
  legacyPageNo,
  setLegacyPageNo,
  legacySequenceNo,
  setLegacySequenceNo,
}: LegacyInstallmentPlanProps) {

  // Equal plan computed values
  const equalComputed = useMemo(() => {
    const totalSale = parseFloat(equalPlan.totalSaleAmount) || 0;
    const previouslyPaid = parseFloat(equalPlan.previouslyPaid) || 0;
    const totalCount = parseInt(equalPlan.totalInstallmentCount, 10) || 0;
    const paidCount = parseInt(equalPlan.paidInstallmentCount, 10) || 0;
    const remaining = Math.max(0, totalSale - previouslyPaid);
    const remainingCount = Math.max(0, totalCount - paidCount);
    const monthly = remainingCount > 0 ? Math.round((remaining / remainingCount) * 100) / 100 : 0;

    return { remaining, remainingCount, monthly, totalSale, totalCount, paidCount };
  }, [equalPlan]);

  // Auto-calculate monthly installment amount when base values change
  useEffect(() => {
    const monthly = equalComputed.monthly;
    const monthlyStr = String(monthly);
    if (monthly > 0 && equalPlan.monthlyInstallmentAmount !== monthlyStr) {
      setEqualPlan(prev => ({ ...prev, monthlyInstallmentAmount: monthlyStr }));
    }
  }, [equalComputed.monthly, equalPlan.monthlyInstallmentAmount, setEqualPlan]);

  // Custom plan totals
  const customTotals = useMemo(() => {
    const totalAmount = customInstallments.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);
    const totalPaid = customInstallments.reduce((sum, r) => sum + (parseFloat(r.paid_amount) || 0), 0);
    const totalRemaining = Math.max(0, totalAmount - totalPaid);
    return { totalAmount, totalPaid, totalRemaining };
  }, [customInstallments]);

  // Validation: only check custom plan remaining against legacy remaining when legacy has a manual value
  const customRemainingMismatch = useMemo(() => {
    if (planType !== 'custom_installments' || customInstallments.length === 0) return false;
    if (legacyRemainingAmount <= 0) return false;
    return Math.abs(customTotals.totalRemaining - legacyRemainingAmount) > 0.01;
  }, [planType, customInstallments, customTotals, legacyRemainingAmount]);

  function updateEqualField(field: keyof EqualPlanData, value: string) {
    setEqualPlan(prev => ({ ...prev, [field]: value }));
    setErrors(prev => {
      const next = { ...prev };
      delete next[`equal_${field}`];
      return next;
    });
  }

  function updateCustomRow(key: string, field: keyof CustomInstallmentRow, value: string) {
    setCustomInstallments(prev => prev.map(row => {
      if (row.key !== key) return row;
      const updated = { ...row, [field]: value };
      if (field === 'amount' || field === 'paid_amount') {
        const amt = parseFloat(updated.amount) || 0;
        const paid = parseFloat(updated.paid_amount) || 0;
        updated.remaining = (Math.max(0, amt - paid)).toFixed(2);
      }
      // Auto-set status based on amount, paid_amount and due_date
      const amt = parseFloat(updated.amount) || 0;
      const paid = parseFloat(updated.paid_amount) || 0;
      const remaining = Math.max(0, amt - paid);
      const today = new Date().toISOString().split('T')[0];
      if (amt > 0 && remaining <= 0.01) {
        updated.status = 'Ödendi';
      } else if (paid > 0 && remaining > 0) {
        updated.status = 'Kısmi Ödendi';
      } else if (updated.due_date && updated.due_date < today) {
        updated.status = 'Gecikti';
      } else {
        updated.status = 'Bekliyor';
      }
      return updated;
    }));
    setErrors(prev => {
      const next = { ...prev };
      delete next[`custom_${key}_${field}`];
      return next;
    });
  }

  function addCustomRow() {
    const seq = customInstallments.length + 1;
    setCustomInstallments(prev => [...prev, {
      key: generateKey(),
      sequence_no: seq,
      due_date: '',
      amount: '',
      paid_amount: '0',
      remaining: '',
      status: 'Bekliyor',
    }]);
  }

  function removeCustomRow(key: string) {
    setCustomInstallments(prev => {
      const filtered = prev.filter(r => r.key !== key);
      return filtered.map((r, i) => ({ ...r, sequence_no: i + 1 }));
    });
  }

  return (
    <div className="space-y-4">
      {/* Payment Plan Type Selector */}
      <div>
        <label className={labelClass}>Ödeme Planı Türü <span className="text-red-400">*</span></label>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {([
            { value: 'single_note' as PaymentPlanType, icon: 'ri-file-list-3-line', label: 'Tek Borç / Senet', desc: 'Tek senet kaydı oluştur' },
            { value: 'equal_installments' as PaymentPlanType, icon: 'ri-equalizer-line', label: 'Eşit Taksit Planı', desc: 'Eşit taksitler otomatik oluşturulur' },
            { value: 'custom_installments' as PaymentPlanType, icon: 'ri-list-check-3', label: 'Özel Taksit Planı', desc: 'Her taksit manuel girilir' },
          ]).map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => { setPlanType(opt.value); setErrors(prev => { const n = { ...prev }; delete n.plan_validation; return n; }); }}
              className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 text-center cursor-pointer transition-all whitespace-nowrap ${
                planType === opt.value
                  ? 'border-amber-500 bg-amber-50 shadow-sm'
                  : 'border-background-200 bg-background-50 hover:border-amber-300 hover:bg-amber-50/50'
              }`}
            >
              <span className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                planType === opt.value ? 'bg-amber-500 text-background-50' : 'bg-amber-100 text-amber-600'
              }`}>
                <i className={`${opt.icon} text-sm`}></i>
              </span>
              <span className="text-xs font-semibold text-foreground-800">{opt.label}</span>
              <span className="text-[10px] text-foreground-400 leading-tight">{opt.desc}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Equal Installment Plan */}
      {planType === 'equal_installments' && (
        <div className="p-4 bg-amber-50/50 border border-amber-200 rounded-xl space-y-3">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-6 h-6 rounded-md bg-amber-500 flex items-center justify-center flex-shrink-0">
              <i className="ri-equalizer-line text-background-50 text-xs"></i>
            </span>
            <h5 className="text-xs font-semibold text-amber-800">Eşit Taksit Planı Detayları</h5>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Toplam Satış Tutarı (₺) <span className="text-red-400">*</span></label>
              <CurrencyInput value={equalPlan.totalSaleAmount}
                onChange={v => updateEqualField('totalSaleAmount', v)}
                className={errors.equal_totalSaleAmount ? inputErrorClass : inputClass} placeholder="0" />
              {errors.equal_totalSaleAmount && <p className={errorTextClass}>{errors.equal_totalSaleAmount}</p>}
            </div>
            <div>
              <label className={labelClass}>Daha Önce Ödenen Tutar (₺)</label>
              <CurrencyInput value={equalPlan.previouslyPaid}
                onChange={v => updateEqualField('previouslyPaid', v)}
                className={inputClass} placeholder="0" />
            </div>
            <div>
              <label className={labelClass}>Kalan Borç (₺)</label>
              <div className={readonlyClass}>{equalComputed.remaining.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺</div>
              <p className={helperTextClass}>Otomatik hesaplanır</p>
            </div>
            <div>
              <label className={labelClass}>Toplam Taksit Sayısı <span className="text-red-400">*</span></label>
              <input type="number" min="1" value={equalPlan.totalInstallmentCount}
                onChange={e => updateEqualField('totalInstallmentCount', e.target.value)}
                className={errors.equal_totalInstallmentCount ? inputErrorClass : inputClass} placeholder="Örn: 12" />
              {errors.equal_totalInstallmentCount && <p className={errorTextClass}>{errors.equal_totalInstallmentCount}</p>}
            </div>
            <div>
              <label className={labelClass}>Ödenmiş Taksit Sayısı</label>
              <input type="number" min="0" value={equalPlan.paidInstallmentCount}
                onChange={e => updateEqualField('paidInstallmentCount', e.target.value)}
                className={inputClass} placeholder="0" />
            </div>
            <div>
              <label className={labelClass}>Kalan Taksit Sayısı</label>
              <div className={readonlyClass}>{equalComputed.remainingCount}</div>
              <p className={helperTextClass}>Otomatik hesaplanır</p>
            </div>
            <div>
              <label className={labelClass}>Aylık Taksit Tutarı (₺)</label>
              <CurrencyInput value={equalPlan.monthlyInstallmentAmount}
                onChange={v => updateEqualField('monthlyInstallmentAmount', v)}
                className={errors.equal_monthlyInstallmentAmount ? inputErrorClass : inputClass}
                placeholder="0" />
              <p className={helperTextClass}>Otomatik hesaplanır: Kalan Borç / Kalan Taksit Sayısı</p>
              {errors.equal_monthlyInstallmentAmount && <p className={errorTextClass}>{errors.equal_monthlyInstallmentAmount}</p>}
            </div>
            <div>
              <label className={labelClass}>İlk Taksit Tarihi <span className="text-red-400">*</span></label>
              <input type="date" value={equalPlan.firstInstallmentDate}
                onChange={e => updateEqualField('firstInstallmentDate', e.target.value)}
                className={errors.equal_firstInstallmentDate ? inputErrorClass : inputClass} />
              {errors.equal_firstInstallmentDate && <p className={errorTextClass}>{errors.equal_firstInstallmentDate}</p>}
            </div>
            <div>
              <label className={labelClass}>Her Ayın Ödeme Günü</label>
              <select value={equalPlan.paymentDayOfMonth}
                onChange={e => updateEqualField('paymentDayOfMonth', e.target.value)}
                className={selectClass}>
                {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
              <p className={helperTextClass}>Ayın kaçıncı günü ödeneceği</p>
            </div>
          </div>

          {/* Preview of generated installments */}
          {equalComputed.totalCount > 0 && equalPlan.firstInstallmentDate && (
            <div className="mt-3 p-3 bg-background-50 rounded-lg border border-background-200">
              <p className="text-xs font-semibold text-foreground-700 mb-2">
                Oluşturulacak Taksit Özeti ({equalComputed.totalCount} taksit)
              </p>
              <div className="space-y-1 text-xs text-foreground-500">
                <div className="flex justify-between">
                  <span>Toplam tutar:</span>
                  <span className="font-medium">{equalComputed.totalSale.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺</span>
                </div>
                <div className="flex justify-between">
                  <span>Toplam ödenen:</span>
                  <span className="font-medium">{equalComputed.paidCount > 0 ? (equalComputed.monthly * equalComputed.paidCount).toLocaleString('tr-TR', { minimumFractionDigits: 2 }) : '0,00'} ₺</span>
                </div>
                <div className="flex justify-between">
                  <span>Toplam kalan:</span>
                  <span className="font-medium">{equalComputed.remaining.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺</span>
                </div>
                <div className="flex justify-between">
                  <span>Ödenmiş taksitler:</span>
                  <span className="font-medium">{equalComputed.paidCount} / {equalComputed.totalCount} adet</span>
                </div>
                <div className="flex justify-between border-t border-background-200 pt-1.5 mt-1.5">
                  <span className="text-foreground-500 font-medium">Devredilen kalan borç:</span>
                  <span className="font-semibold text-foreground-800">{equalComputed.remaining.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺</span>
                </div>
              </div>
            </div>
          )}

          {/* Senet Bilgileri (eşit taksitli plan için otomatik senet) */}
          <div className="mt-3 pt-3 border-t border-amber-200">
            <div className="flex items-center gap-2 mb-3">
              <span className="w-6 h-6 rounded-md bg-amber-600 flex items-center justify-center flex-shrink-0">
                <i className="ri-file-list-3-line text-background-50 text-xs"></i>
              </span>
              <h5 className="text-xs font-semibold text-amber-800">Senet Bilgileri (Otomatik Oluşturulacak)</h5>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Eski Senet No</label>
                <input type="text" value={legacyNoteNo}
                  onChange={e => { setLegacyNoteNo(e.target.value); setErrors(prev => ({ ...prev, legacy_note_no: '' })); }}
                  className={errors.legacy_note_no ? inputErrorClass : inputClass} placeholder="Boş bırakılırsa otomatik" />
                <p className={helperTextClass}>Boş bırakılırsa otomatik atanır</p>
              </div>
              <div>
                <label className={labelClass}>Kitap No <span className="text-red-400">*</span></label>
                <input type="text" value={legacyBookNo}
                  onChange={e => { setLegacyBookNo(e.target.value); setErrors(prev => ({ ...prev, legacy_book_no: '' })); }}
                  className={errors.legacy_book_no ? inputErrorClass : inputClass} placeholder="Örn: Kitap-1" />
                {errors.legacy_book_no && <p className={errorTextClass}>{errors.legacy_book_no}</p>}
              </div>
              <div>
                <label className={labelClass}>Sayfa No <span className="text-red-400">*</span></label>
                <input type="text" value={legacyPageNo}
                  onChange={e => { setLegacyPageNo(e.target.value); setErrors(prev => ({ ...prev, legacy_page_no: '' })); }}
                  className={errors.legacy_page_no ? inputErrorClass : inputClass} placeholder="Örn: 15" />
                {errors.legacy_page_no && <p className={errorTextClass}>{errors.legacy_page_no}</p>}
              </div>
              <div>
                <label className={labelClass}>Sıra No</label>
                <input type="text" value={legacySequenceNo}
                  onChange={e => setLegacySequenceNo(e.target.value)}
                  className={inputClass} placeholder="Opsiyonel" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom Installment Plan */}
      {planType === 'custom_installments' && (
        <div className="p-4 bg-amber-50/50 border border-amber-200 rounded-xl space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="w-6 h-6 rounded-md bg-amber-500 flex items-center justify-center flex-shrink-0">
                <i className="ri-list-check-3 text-background-50 text-xs"></i>
              </span>
              <h5 className="text-xs font-semibold text-amber-800">Özel Taksit Planı</h5>
            </div>
            <button type="button" onClick={addCustomRow}
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg bg-amber-500 text-background-50 hover:bg-amber-600 cursor-pointer whitespace-nowrap transition-colors">
              <i className="ri-add-line"></i> Taksit Ekle
            </button>
          </div>

          {customInstallments.length === 0 ? (
            <p className="text-xs text-foreground-400 text-center py-4">Henüz taksit eklenmedi. "Taksit Ekle" butonuna tıklayın.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-amber-200">
                    <th className="text-left py-2 px-1 text-foreground-500 font-medium whitespace-nowrap">#</th>
                    <th className="text-left py-2 px-1 text-foreground-500 font-medium whitespace-nowrap">Vade Tarihi</th>
                    <th className="text-left py-2 px-1 text-foreground-500 font-medium whitespace-nowrap">Tutar (₺)</th>
                    <th className="text-left py-2 px-1 text-foreground-500 font-medium whitespace-nowrap">Ödenen (₺)</th>
                    <th className="text-left py-2 px-1 text-foreground-500 font-medium whitespace-nowrap">Kalan (₺)</th>
                    <th className="text-left py-2 px-1 text-foreground-500 font-medium whitespace-nowrap">Durum</th>
                    <th className="w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {customInstallments.map(row => (
                    <tr key={row.key} className="border-b border-background-100">
                      <td className="py-1.5 px-1 text-foreground-600">{row.sequence_no}</td>
                      <td className="py-1.5 px-1">
                        <input type="date" value={row.due_date}
                          onChange={e => updateCustomRow(row.key, 'due_date', e.target.value)}
                          className="w-full px-1.5 py-1 text-xs border border-background-200 rounded focus:outline-none focus:border-amber-400" />
                      </td>
                      <td className="py-1.5 px-1">
                        <CurrencyInput value={row.amount}
                          onChange={v => updateCustomRow(row.key, 'amount', v)}
                          className="w-20 px-1.5 py-1 text-xs border border-background-200 rounded focus:outline-none focus:border-amber-400" />
                      </td>
                      <td className="py-1.5 px-1">
                        <CurrencyInput value={row.paid_amount}
                          onChange={v => updateCustomRow(row.key, 'paid_amount', v)}
                          className="w-20 px-1.5 py-1 text-xs border border-background-200 rounded focus:outline-none focus:border-amber-400" />
                      </td>
                      <td className="py-1.5 px-1 text-foreground-600">
                        {parseFloat(row.remaining || '0').toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="py-1.5 px-1">
                        <select value={row.status}
                          onChange={e => updateCustomRow(row.key, 'status', e.target.value)}
                          className="w-full px-1 py-1 text-xs border border-background-200 rounded focus:outline-none focus:border-amber-400 cursor-pointer">
                          {STATUS_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </td>
                      <td className="py-1.5 px-1">
                        <button type="button" onClick={() => removeCustomRow(row.key)}
                          className="w-6 h-6 flex items-center justify-center rounded hover:bg-red-50 text-foreground-400 hover:text-red-500 cursor-pointer">
                          <i className="ri-delete-bin-line text-xs"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Custom plan summary */}
          {customInstallments.length > 0 && (
            <div className="mt-2 p-3 rounded-lg border text-xs bg-background-50 border-background-200">
              <div className="flex justify-between">
                <span className="text-foreground-500">Toplam tutar:</span>
                <span className="font-medium">{customTotals.totalAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺</span>
              </div>
              <div className="flex justify-between">
                <span className="text-foreground-500">Toplam ödenen:</span>
                <span className="font-medium">{customTotals.totalPaid.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺</span>
              </div>
              <div className="flex justify-between">
                <span className="text-foreground-500">Toplam kalan:</span>
                <span className="font-medium">
                  {customTotals.totalRemaining.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                </span>
              </div>
              <div className="flex justify-between border-t border-background-200 pt-1.5 mt-1.5">
                <span className="text-foreground-500 font-medium">Devredilen kalan borç:</span>
                <span className="font-semibold text-foreground-800">{customTotals.totalRemaining.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺</span>
              </div>
            </div>
          )}

          {/* Senet Bilgileri (özel taksitli plan için otomatik senet) */}
          <div className="mt-3 pt-3 border-t border-amber-200">
            <div className="flex items-center gap-2 mb-3">
              <span className="w-6 h-6 rounded-md bg-amber-600 flex items-center justify-center flex-shrink-0">
                <i className="ri-file-list-3-line text-background-50 text-xs"></i>
              </span>
              <h5 className="text-xs font-semibold text-amber-800">Senet Bilgileri (Otomatik Oluşturulacak)</h5>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Eski Senet No</label>
                <input type="text" value={legacyNoteNo}
                  onChange={e => { setLegacyNoteNo(e.target.value); setErrors(prev => ({ ...prev, legacy_note_no: '' })); }}
                  className={errors.legacy_note_no ? inputErrorClass : inputClass} placeholder="Boş bırakılırsa otomatik" />
                <p className={helperTextClass}>Boş bırakılırsa otomatik atanır</p>
              </div>
              <div>
                <label className={labelClass}>Kitap No <span className="text-red-400">*</span></label>
                <input type="text" value={legacyBookNo}
                  onChange={e => { setLegacyBookNo(e.target.value); setErrors(prev => ({ ...prev, legacy_book_no: '' })); }}
                  className={errors.legacy_book_no ? inputErrorClass : inputClass} placeholder="Örn: Kitap-1" />
                {errors.legacy_book_no && <p className={errorTextClass}>{errors.legacy_book_no}</p>}
              </div>
              <div>
                <label className={labelClass}>Sayfa No <span className="text-red-400">*</span></label>
                <input type="text" value={legacyPageNo}
                  onChange={e => { setLegacyPageNo(e.target.value); setErrors(prev => ({ ...prev, legacy_page_no: '' })); }}
                  className={errors.legacy_page_no ? inputErrorClass : inputClass} placeholder="Örn: 15" />
                {errors.legacy_page_no && <p className={errorTextClass}>{errors.legacy_page_no}</p>}
              </div>
              <div>
                <label className={labelClass}>Sıra No</label>
                <input type="text" value={legacySequenceNo}
                  onChange={e => setLegacySequenceNo(e.target.value)}
                  className={inputClass} placeholder="Opsiyonel" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Validation errors */}
      {errors.plan_validation && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-xs text-red-600 flex items-center gap-1.5">
            <i className="ri-error-warning-line"></i> {errors.plan_validation}
          </p>
        </div>
      )}
    </div>
  );
}

export function generateEqualPlanInstallments(
  equalPlan: EqualPlanData,
  customerId: string,
  saleId: string,
): { installments: any[]; legacyPayments: any[] } {
  const totalSale = parseFloat(equalPlan.totalSaleAmount) || 0;
  const totalCount = parseInt(equalPlan.totalInstallmentCount, 10) || 0;
  const paidCount = parseInt(equalPlan.paidInstallmentCount, 10) || 0;
  const monthlyAmount = parseFloat(equalPlan.monthlyInstallmentAmount) || 0;
  const firstDate = equalPlan.firstInstallmentDate;
  const payDay = parseInt(equalPlan.paymentDayOfMonth, 10) || 1;

  if (totalCount <= 0 || monthlyAmount <= 0 || !firstDate) {
    return { installments: [], legacyPayments: [] };
  }

  const installments: any[] = [];
  const legacyPayments: any[] = [];

  const [year, month] = firstDate.split('-').map(Number);

  for (let i = 0; i < totalCount; i++) {
    const instMonth = month + i;
    const instYear = year + Math.floor((instMonth - 1) / 12);
    const actualMonth = ((instMonth - 1) % 12) + 1;
    const lastDay = new Date(instYear, actualMonth, 0).getDate();
    const day = Math.min(payDay, lastDay);
    const dueDate = `${instYear}-${String(actualMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

    const isPaid = i < paidCount;
    const paidAmount = isPaid ? monthlyAmount : 0;
    const remaining = isPaid ? 0 : monthlyAmount;
    const status = isPaid ? 'Ödendi' : 'Bekliyor';
    const today = new Date().toISOString().split('T')[0];

    installments.push({
      customer_id: customerId,
      sale_id: saleId,
      installment_no: i + 1,
      sequence_no: i + 1,
      due_date: dueDate,
      amount: monthlyAmount,
      original_amount: monthlyAmount,
      paid_amount: paidAmount,
      remaining_amount: remaining,
      status: (!isPaid && dueDate < today) ? 'Gecikti' : status,
      paid_date: isPaid ? dueDate : null,
      plan_type: 'equal',
      description: `Eski sistem devir - ${i + 1}. taksit`,
    });

    if (isPaid) {
      legacyPayments.push({
        customer_id: customerId,
        sale_id: saleId,
        payment_date: dueDate,
        amount: monthlyAmount,
        payment_method: 'Devir Kaydı',
        receipt_no: null, // Will be generated later
        description: `Eski sistem - ${i + 1}. taksit ödemesi`,
        received_by: 'Sistem',
        is_legacy_payment: true,
      });
    }
  }

  return { installments, legacyPayments };
}

export function generateCustomPlanInstallments(
  customInstallments: CustomInstallmentRow[],
  customerId: string,
  saleId: string,
): { installments: any[]; legacyPayments: any[]; totalAmount: number; totalPaid: number; totalRemaining: number } {
  const installments: any[] = [];
  const legacyPayments: any[] = [];
  let totalAmount = 0;
  let totalPaid = 0;
  let totalRemaining = 0;

  customInstallments.forEach(row => {
    const amount = parseFloat(row.amount) || 0;
    const paid = parseFloat(row.paid_amount) || 0;
    const remaining = Math.max(0, amount - paid);

    totalAmount += amount;
    totalPaid += paid;
    totalRemaining += remaining;

    const today = new Date().toISOString().split('T')[0];
    let status = row.status;
    if (status === 'Bekliyor' && row.due_date && row.due_date < today && remaining > 0) {
      status = 'Gecikti';
    }

    installments.push({
      customer_id: customerId,
      sale_id: saleId,
      installment_no: row.sequence_no,
      sequence_no: row.sequence_no,
      due_date: row.due_date || null,
      amount,
      original_amount: amount,
      paid_amount: paid,
      remaining_amount: remaining,
      status,
      paid_date: paid > 0 ? row.due_date : null,
      plan_type: 'custom',
      description: `Eski sistem devir - ${row.sequence_no}. taksit`,
    });

    if (paid > 0) {
      legacyPayments.push({
        customer_id: customerId,
        sale_id: saleId,
        payment_date: row.due_date || new Date().toISOString().split('T')[0],
        amount: paid,
        payment_method: 'Devir Kaydı',
        receipt_no: null,
        description: `Eski sistem - ${row.sequence_no}. taksit ödemesi`,
        received_by: 'Sistem',
        is_legacy_payment: true,
      });
    }
  });

  return { installments, legacyPayments, totalAmount, totalPaid, totalRemaining };
}